import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { challengeService } from '../../../services/socialService';


const ChallengeCard = ({ challenge, userId, onJoin, index }) => {
  const [participants, setParticipants] = useState([]);
  const [userParticipation, setUserParticipation] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchParticipants();
  }, [challenge?.id]);

  const fetchParticipants = async () => {
    try {
      const data = await challengeService?.getChallengeParticipants(challenge?.id);
      setParticipants(data);
      const userPart = data?.find(p => p?.user_id === userId);
      setUserParticipation(userPart);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const handleJoin = async () => {
    setLoading(true);
    try {
      await onJoin(challenge?.id);
      await fetchParticipants();
    } catch (error) {
      console.error('Error joining challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const colorMap = {
    success: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', icon: 'bg-success' },
    accent: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent', icon: 'bg-accent' },
    secondary: { bg: 'bg-secondary/10', border: 'border-secondary/30', text: 'text-secondary', icon: 'bg-secondary' },
    warning: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', icon: 'bg-warning' },
    primary: { bg: 'bg-primary/10', border: 'border-primary/30', text: 'text-primary', icon: 'bg-primary' }
  };

  const colors = colorMap?.[challenge?.color] || colorMap?.primary;
  const completedCount = participants?.filter(p => p?.is_completed)?.length || 0;
  const progressPercentage = userParticipation ? Math?.min((userParticipation?.current_progress / challenge?.target_value) * 100, 100) : 0;
  const daysLeft = Math?.ceil((new Date(challenge?.end_date) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`relative p-6 rounded-xl border-2 ${colors?.bg} ${colors?.border} hover:shadow-lg transition-all duration-300`}
    >
      {/* Challenge Icon */}
      <div className={`w-12 h-12 rounded-full ${colors?.icon} flex items-center justify-center mb-4`}>
        <Icon name={challenge?.icon} size={24} color="white" />
      </div>
      {/* Challenge Info */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">{challenge?.title}</h3>
        <p className="text-sm text-muted-foreground mb-3">{challenge?.description}</p>
        
        {/* Target */}
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Target" size={16} className={colors?.text} />
          <span className="text-sm font-medium text-foreground">
            {challenge?.target_value} {challenge?.unit}
          </span>
        </div>

        {/* Time Left */}
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {daysLeft > 0 ? `${daysLeft} days left` : 'Ending today'}
          </span>
        </div>
      </div>
      {/* Participants */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Participants</span>
          <span className="text-sm font-medium text-foreground">{participants?.length}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={16} className={colors?.text} />
          <span className="text-sm text-muted-foreground">
            {completedCount} completed
          </span>
        </div>
      </div>
      {/* User Progress or Join Button */}
      {userParticipation ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Your Progress</span>
              <span className={`font-medium ${colors?.text}`}>
                {progressPercentage?.toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-muted/50 rounded-full h-2">
              <motion.div
                className={`h-2 rounded-full ${colors?.icon}`}
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {userParticipation?.current_progress} / {challenge?.target_value} {challenge?.unit}
            </p>
          </div>
          {userParticipation?.is_completed && (
            <div className="flex items-center space-x-2 p-2 bg-success/10 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Challenge Completed!</span>
            </div>
          )}
        </div>
      ) : (
        <Button
          onClick={handleJoin}
          loading={loading}
          disabled={loading}
          variant="default"
          size="default"
          iconName="Plus"
          fullWidth
          className="mt-2"
        >
          Join Challenge
        </Button>
      )}
    </motion.div>
  );
};

export default ChallengeCard;